import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { validateEmail } from "../../services/ApiCaller/Registration";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import lazyCall from "../../services/apiLazy";
import { decryptWithSpaces } from "../../util/DecryptWithSpaces";
import CustomizedDropdown from "../../util/CustomizedDropdown";
import "../layout/accessibility.css";
import DspFooter from "./DspFooter";
import DspHeader from "./DspHeader";
import EmailWarningModal from "./EmailWarningModal";
import NotificationModal from "./NotificationModal";
import "./Reg2.css";
import "./SkeletonLoader.css";

// Skeleton loader component for registration page
const Reg2Skeleton = () => (
  <div className="container-lg">
    <header className="reg2-hero">
      <div className="reg2-hero-inner">
        <div
          className="skeleton-header"
          style={{ width: "80px", height: "20px", marginBottom: "16px" }}
        />
        <div
          className="skeleton-header"
          style={{ width: "60%", height: "36px", marginBottom: "12px" }}
        />
        <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
          <div
            className="skeleton-line"
            style={{ width: "120px", height: "16px" }}
          />
          <div
            className="skeleton-line"
            style={{ width: "120px", height: "16px" }}
          />
          <div
            className="skeleton-line"
            style={{ width: "120px", height: "16px" }}
          />
          <div
            className="skeleton-line"
            style={{ width: "120px", height: "16px" }}
          />
          <div
            className="skeleton-line"
            style={{ width: "120px", height: "16px" }}
          />
        </div>
      </div>
    </header>

    <main className="reg2-main">
      <div
        className="skeleton-header"
        style={{ width: "200px", height: "28px", marginBottom: "24px" }}
      />
      <div
        className="reg2-form"
        style={{ background: "#fff", padding: "32px", borderRadius: "8px" }}
      >
        <div className="reg2-row">
          <div style={{ flex: 1 }}>
            <div
              className="skeleton-line"
              style={{ width: "120px", height: "16px", marginBottom: "8px" }}
            />
            <div
              className="skeleton-header"
              style={{ width: "100%", height: "48px" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div
              className="skeleton-line"
              style={{ width: "120px", height: "16px", marginBottom: "8px" }}
            />
            <div
              className="skeleton-header"
              style={{ width: "100%", height: "48px" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div
              className="skeleton-line"
              style={{ width: "120px", height: "16px", marginBottom: "8px" }}
            />
            <div
              className="skeleton-header"
              style={{ width: "100%", height: "48px" }}
            />
          </div>
        </div>
        <div className="reg2-row" style={{ marginTop: "24px" }}>
          <div style={{ flex: 1 }}>
            <div
              className="skeleton-line"
              style={{ width: "120px", height: "16px", marginBottom: "8px" }}
            />
            <div
              className="skeleton-header"
              style={{ width: "100%", height: "48px" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div
              className="skeleton-line"
              style={{ width: "120px", height: "16px", marginBottom: "8px" }}
            />
            <div
              className="skeleton-header"
              style={{ width: "100%", height: "48px" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div
              className="skeleton-line"
              style={{ width: "120px", height: "16px", marginBottom: "8px" }}
            />
            <div
              className="skeleton-header"
              style={{ width: "100%", height: "48px" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div
              className="skeleton-line"
              style={{ width: "120px", height: "16px", marginBottom: "8px" }}
            />
            <div
              className="skeleton-header"
              style={{ width: "100%", height: "48px" }}
            />
          </div>
        </div>
        <div style={{ marginTop: "32px" }}>
          <div
            className="skeleton-btn"
            style={{ width: "150px", height: "48px" }}
          />
        </div>
      </div>
    </main>
  </div>
);

// Helper function to split name into parts
const getNameParts = (fullName) => {
  const nameArray = (fullName || "").trim().split(/\s+/);
  return {
    firstName: nameArray[0] || "",
    middleName: nameArray.length > 2 ? nameArray[1] : "",
    lastName:
      nameArray.length > 2
        ? nameArray[2]
        : nameArray.length > 1
          ? nameArray[1]
          : "",
  };
};

export default function Reg2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [occupations, setOccupations] = useState([]);
  const [emailValidating, setEmailValidating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("success"); // "success" or "error"
  const [modalMessage, setModalMessage] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [showEmailWarningModal, setShowEmailWarningModal] = useState(false);
  const [conflictEmail, setConflictEmail] = useState("");

  // Cache for email validation results (5 minutes TTL)
  const emailValidationCache = useRef({});
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      ParticipantName: "",
      Email: "",
      Mobile: "",
      occupation: "",
      age: "",
      Gender: "",
      PINcode: "",
    },
    mode: "onChange", // Validate on input so `isValid` updates live
  });

  // Function to fetch event data
  const fetchEventData = useCallback(async () => {
    try {
      setLoading(true);
      const [event, occupationsRes] = await Promise.all([
        lazyCall(
          () => import("../../util/axiosConfig/api/api"),
          "fetchSingleEvent",
          [id],
          { memoize: false }, // Don't use cache when refreshing
        ),
        lazyCall(
          () => import("../../util/axiosConfig/api/api"),
          "fetchOccupations",
          [],
          { memoize: true, ttl: 24 * 60 * 60 * 1000 },
        ),
      ]);

      let trainerName = event?.trainerName || "";
      if (trainerName) {
        try {
          trainerName = await decryptWithSpaces(trainerName);
        } catch (e) {}
      }
      setEventData({ ...event, trainerName });

      const fetchedList = Object.values(occupationsRes || {}).map((item) => ({
        label: item.occupationLebel,
        value: item.occupation,
      }));
      setOccupations([
        { label: "Choose an option", value: "" },
        ...fetchedList,
      ]);
    } catch (e) {
      setModalType("error");
      setModalTitle("Error Loading Event");
      setModalMessage("Failed to load event details. Please try again later.");
      setShowModal(true);
      setOccupations([{ label: "Choose an option", value: "" }]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  // Handler for "Use Existing Data" button
  const handleUseExistingData = async () => {
    setShowEmailWarningModal(false);
    setSubmitting(true);

    const formData = getValues();
    const formattedDOB = null;
    const { firstName, middleName, lastName } = getNameParts(
      formData.ParticipantName,
    );

    const Data = JSON.stringify({
      firstName: firstName,
      middleName: middleName,
      lastName: lastName,
      studentEmail: formData.Email,
      studentMobileNumber: formData.Mobile,
      selfRegistered: true,
      age: parseInt(formData.age, 10),
      occupation: formData.occupation || null,
      studentDOB: formattedDOB,
      pincode: formData.PINcode,
      gender: formData.Gender,
    });

    try {
      await lazyCall(
        () => import("../../util/axiosConfig/api/api"),
        "registerEXUser",
        [id, Data],
        { memoize: false },
      );

      // Show success modal
      setModalType("success");
      setModalTitle("");
      setModalMessage("");
      setShowModal(true);

      // Reset form
      reset({
        Email: "",
        ParticipantName: "",
        Mobile: "",
        Gender: "",
        PINcode: "",
        age: "",
        occupation: null,
        selectedDate: null,
      });
      setConflictEmail("");
      setSubmitting(false);
    } catch (error) {
      const fallbackMessage =
        "We are temporarily experiencing technical difficulties. Please try again after sometime";

      const errorMessage =
        (error && error.data && error.data.message) ||
        (error && error.message) ||
        fallbackMessage;

      setModalType("error");
      setModalTitle("Registration Failed");
      setModalMessage(errorMessage);
      setShowModal(true);

      setSubmitting(false);
    }
  };

  // Handler for "Enter New Details" button
  const handleEnterNewDetails = () => {
    setShowEmailWarningModal(false);
    setValue("Email", "");
    setConflictEmail("");
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    const formattedDOB = null; // No date picker in form
    const { firstName, middleName, lastName } = getNameParts(
      data.ParticipantName,
    );
    const Data = JSON.stringify({
      firstName: firstName,
      middleName: middleName,
      lastName: lastName,
      studentEmail: data.Email,
      studentMobileNumber: data.Mobile,
      selfRegistered: true,
      age: parseInt(data.age, 10),
      occupation: data.occupation || null,
      studentDOB: formattedDOB,
      pincode: data.PINcode,
      gender: data.Gender,
      disclaimer: data.disclaimer,
    });
    try {
      await lazyCall(
        () => import("../../util/axiosConfig/api/api"),
        "registerUser",
        [id, Data],
        { memoize: false },
      );

      // Show success modal
      setModalType("success");
      setModalTitle("");
      setModalMessage("");
      setShowModal(true);

      // Reset form
      reset({
        Email: "",
        ParticipantName: "",
        Mobile: "",
        Gender: "",
        PINcode: "",
        age: "",
        occupation: null,
        selectedDate: null,
      });
      setSubmitting(false);
    } catch (error) {
      const fallbackMessage =
        "We are temporarily experiencing technical difficulties. Please try again after sometime";

      const errorMessage =
        (error && error.data && error.data.message) ||
        (error && error.message) ||
        fallbackMessage;

      setModalType("error");
      setModalTitle("Registration Failed");
      setModalMessage(errorMessage);
      setShowModal(true);

      // Don't reset form on error - let user correct their data
      setSubmitting(false);
    }
  };

  const isOnline = eventData?.offline === false;
  const trainer = eventData?.trainerName || "—";
  const language = eventData?.language || "—";
  const dateStr = eventData?.startTime
    ? moment(eventData.startTime).format("DD MMM, YYYY")
    : "—";
  const timeStr = eventData?.startTime
    ? moment(eventData.startTime).format("hh:mm A") + " IST"
    : "—";
  const availableSlots = eventData?.slotsAvailable || eventData?.slots || "—";

  return (
    <div className="reg2-page">
      {/* Top Navigation Header */}
      <DspHeader />

      {/* Show skeleton loader while loading */}
      {loading && !eventData ? (
        <Reg2Skeleton />
      ) : (
        <>
          {/* Hero Section */}
          <div className="container-lg mt-4">
            <header className="reg2-hero">
              <div className="reg2-hero-inner">
                <button
                  type="button"
                  className="reg2-back"
                  onClick={() => navigate("/")}
                >
                  <ArrowBackIcon className="type-icon" /> Back
                </button>
                <div className="reg2-title-row">
                  <h1 className="reg2-title">
                    {eventData?.eventTopicName || "Event Registration"}
                  </h1>
                  {eventData && (
                    <>
                      {isOnline ? (
                        <span className="reg2-badge reg2-badge-online">
                          <DesktopWindowsOutlinedIcon
                            className="reg2-badge-icon"
                            fontSize="md"
                          />{" "}
                          Online
                        </span>
                      ) : (
                        <div className="reg2-venue-inline">
                          <div className="reg2-venue-inline-text">
                            {eventData?.place && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "8px",
                                }}
                              >
                                <FmdGoodOutlinedIcon
                                  className="reg2-venue-inline-icon"
                                  fontSize="md"
                                />
                                <span
                                  className="reg2-venue-inline-name"
                                  onClick={() => {
                                    if (
                                      eventData.mapLink &&
                                      eventData.mapLink !== "null"
                                    ) {
                                      window.open(
                                        eventData.mapLink,
                                        "_blank",
                                        "noopener,noreferrer",
                                      );
                                    }
                                  }}
                                  style={{
                                    cursor:
                                      eventData.mapLink &&
                                      eventData.mapLink !== "null"
                                        ? "pointer"
                                        : "default",
                                  }}
                                >
                                  {eventData.place}
                                </span>
                              </div>
                            )}
                            {eventData?.state && (
                              <span className="reg2-venue-inline-address">
                                {eventData.state} - {eventData.pincode || ""}
                              </span>
                            )}
                            {!eventData?.state && !eventData?.place && (
                              <span>Offline Event</span>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="reg2-main-div">
                  <div className="reg2-meta">
                    <div>
                      <strong>Trainer</strong>
                      <div>{trainer}</div>
                    </div>
                    <div>
                      <strong>Language</strong>
                      <div>{language}</div>
                    </div>
                    <div>
                      <strong>Date</strong>
                      <div>{dateStr}</div>
                    </div>
                    <div>
                      <strong>Time</strong>
                      <div>{timeStr}</div>
                    </div>
                  </div>
                  {/* {!isOnline && (eventData?.venue || eventData?.address) && (
                  <div className="reg2-venue-card">
                    <FmdGoodOutlinedIcon className="reg2-venue-icon" />
                    <div className="reg2-venue-content">
                      {eventData?.venue && (
                        <div className="reg2-venue-name">
                          {eventData.venue}
                        </div>
                      )}
                      {eventData?.address && (
                        <div className="reg2-venue-address">
                          {eventData.address}
                        </div>
                      )}
                    </div>
                  </div>
                )} */}
                  <div className="reg2-availability">
                    <span className="available_slots">Available slots</span>
                    <div>{availableSlots}</div>
                  </div>
                </div>
              </div>
            </header>

            <main className="reg2-main">
              <h3 className="reg2-section-title">Registration Form</h3>
              <form className="reg2-form" onSubmit={handleSubmit(onSubmit)}>
                <div className="row reg2-row">
                  <div className="col-md-4 col-12">
                    <label>
                      <span className="label-text">
                        Participant Email <span className="required">*</span>
                      </span>
                      <Controller
                        name="Email"
                        control={control}
                        rules={{
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          },
                          validate: async (value) => {
                            try {
                              setEmailValidating(true);

                              // Check cache first
                              const cached =
                                emailValidationCache.current[value];
                              const now = Date.now();

                              let exists;
                              if (
                                cached &&
                                now - cached.timestamp < CACHE_TTL
                              ) {
                                // Use cached result
                                exists = cached.result;
                              } else {
                                // Call API and cache result
                                exists = await validateEmail(value);
                                emailValidationCache.current[value] = {
                                  result: exists,
                                  timestamp: now,
                                };
                              }

                              setEmailValidating(false);

                              // Handle response: if email is already registered, show warning
                              if (exists.present === true) {
                                // Show email warning modal with options
                                setConflictEmail(value);
                                setShowEmailWarningModal(true);
                                return "This email is already registered. Please choose an option.";
                              }

                              // Email is not registered - valid
                              return true;
                            } catch (error) {
                              // If API fails, allow the form to proceed
                              setEmailValidating(false);
                              return true;
                            }
                          },
                        }}
                        render={({ field }) => (
                          <div style={{ position: "relative" }}>
                            <input
                              {...field}
                              type="email"
                              placeholder="name@domain.com"
                              className={errors.Email ? "error" : ""}
                              style={{ width: "100%" }}
                            />
                            {emailValidating && (
                              <span
                                style={{
                                  position: "absolute",
                                  right: "12px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  color: "#999",
                                }}
                              >
                                <i className="fa fa-spinner fa-spin"></i>
                              </span>
                            )}
                          </div>
                        )}
                      />
                      {errors.Email && (
                        <span className="error-message">
                          {errors.Email.message}
                        </span>
                      )}
                    </label>
                  </div>
                  <div className="col-md-4 col-12">
                    <label>
                      <span className="label-text">
                        Participant Name <span className="required">*</span>
                      </span>
                      <Controller
                        name="ParticipantName"
                        control={control}
                        rules={{
                          required: "Name is required",
                          minLength: {
                            value: 2,
                            message: "Name must be at least 2 characters",
                          },
                          pattern: {
                            value: /^[a-zA-Z\s]+$/,
                            message: "Name can only contain letters",
                          },
                        }}
                        render={({ field }) => (
                          <input
                            {...field}
                            placeholder="Full name"
                            className={errors.ParticipantName ? "error" : ""}
                          />
                        )}
                      />
                      {errors.ParticipantName && (
                        <span className="error-message">
                          {errors.ParticipantName.message}
                        </span>
                      )}
                    </label>
                  </div>
                  <div className="col-md-4 col-sm-4 col-3">
                    <label>
                      <span className="label-text">
                        Age <span className="required">*</span>
                      </span>
                      <Controller
                        name="age"
                        control={control}
                        rules={{
                          required: "Age is required",
                          min: {
                            value: 10,
                            message: "Minimum age is 10",
                          },
                          max: {
                            value: 99,
                            message: "Maximum age is 99",
                          },
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "Age must be a number",
                          },
                        }}
                        render={({ field: { onChange, ...restField } }) => (
                          <input
                            {...restField}
                            type="number"
                            placeholder="Age"
                            className={errors.age ? "error" : ""}
                            onChange={(e) => {
                              // Force the value to a maximum of 2 characters
                              const value = e.target.value.slice(0, 2);
                              onChange(value);
                            }}
                          />
                        )}
                      />
                      {errors.age && (
                        <span className="error-message">
                          {errors.age.message}
                        </span>
                      )}
                    </label>
                  </div>

                  <div className="col-md-3 col-sm-8 col-5 form-field-margin">
                    <Controller
                      name="Gender"
                      control={control}
                      rules={{
                        required: "Gender is required",
                        validate: (value) =>
                          value !== "" || "Please select a gender",
                      }}
                      render={({ field }) => (
                        <CustomizedDropdown
                          label={
                            <span className="label-text">
                              Gender <span className="required">*</span>
                            </span>
                          }
                          placeholder="Choose an option"
                          value={field.value}
                          onChange={field.onChange}
                          options={[
                            { label: "Male", value: "102002" },
                            { label: "Female", value: "102001" },
                          ]}
                        />
                      )}
                    />
                    {errors.Gender && (
                      <span className="error-message">
                        {errors.Gender.message}
                      </span>
                    )}
                  </div>
                  <div className="col-md-3 col-12 form-field-margin">
                    <label>
                      <span className="label-text">
                        Mobile <span className="required">*</span>
                      </span>
                      <div
                        className={`mobile-input-wrapper ${errors.Mobile ? "error" : ""}`}
                      >
                        <span className="mobile-prefix">+91</span>
                        <Controller
                          name="Mobile"
                          control={control}
                          rules={{
                            required: "Mobile number is required",
                            pattern: {
                              value: /^[6-9]\d{9}$/,
                              message:
                                "Invalid mobile number (10 digits starting with 6-9)",
                            },
                          }}
                          render={({ field }) => (
                            <input
                              {...field}
                              type="tel"
                              placeholder="99999 99999"
                              maxLength={10}
                            />
                          )}
                        />
                      </div>
                      {errors.Mobile && (
                        <span className="error-message">
                          {errors.Mobile.message}
                        </span>
                      )}
                    </label>
                  </div>
                  <div className="col-md-3 col-12 form-field-margin">
                    <Controller
                      name="occupation"
                      control={control}
                      render={({ field }) => (
                        <CustomizedDropdown
                          label="Occupation"
                          placeholder="Choose an option"
                          value={field.value}
                          onChange={field.onChange}
                          options={occupations.filter((o) => o.value !== "")}
                        />
                      )}
                    />
                    {errors.occupation && (
                      <span className="error-message">
                        {errors.occupation.message}
                      </span>
                    )}
                  </div>
                  <div className="col-md-3 col-12 form-field-margin">
                    <label>
                      <span className="label-text">
                        Pincode <span className="required">*</span>
                      </span>
                      <Controller
                        name="PINcode"
                        control={control}
                        rules={{
                          required: "Pincode is required",
                          pattern: {
                            value: /^[1-9][0-9]{5}$/,
                            message: "Invalid pincode (6 digits)",
                          },
                        }}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="text"
                            placeholder="Pincode"
                            maxLength={6}
                            className={errors.PINcode ? "error" : ""}
                          />
                        )}
                      />
                      {errors.PINcode && (
                        <span className="error-message">
                          {errors.PINcode.message}
                        </span>
                      )}
                    </label>
                  </div>
                </div>

                <div className="reg2-actions">
                  <button
                    className="reg2-btn"
                    type="submit"
                    disabled={!isValid}
                  >
                    {submitting ? "Registering..." : "Register"}
                  </button>
                </div>
              </form>
            </main>
          </div>
        </>
      )}

      {/* Footer */}
      <DspFooter />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showModal}
        type={modalType}
        message={modalMessage}
        title={modalTitle}
        onClose={async () => {
          setShowModal(false);
          setModalMessage("");
          setModalTitle("");
          if (modalType === "success") {
            await fetchEventData(); // Refresh event data after registration
          }
        }}
      />

      {/* Email Warning Modal */}
      <EmailWarningModal
        isOpen={showEmailWarningModal}
        email={conflictEmail}
        onUseExisting={handleUseExistingData}
        onEnterNew={handleEnterNewDetails}
      />
    </div>
  );
}
